import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout'
import { AfterContentInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ElementRef } from '@angular/core'
import { FormGroup } from '@angular/forms'
import { Auth0Error } from 'auth0-js'
import { getCookie, generateId, UnsubscribeOnDestroy, scrollToTop } from '../../../../utility'
import {
    SignupHotelGQL,
    SignupHotelRequest,
    SignupTravelAgentGQL,
    SignupTravelAgentRequest,
    TrackingContext,
    Role,
    AnnualBookingRevenue
} from '../../../../../graphql'
import { environment } from '../../../../../environments/environment'
import { onboardingStorageKeys, socialConnections, breakpoints } from '../../../../constant'
import { Amplitude, Auth } from '../../../../injectable'
import { LegalDocumentType } from '../legal-document-links/legal-document-links'
import { tabButtonData } from '../tabs/tabs'

export enum SelfSignupRoles {
    hotel = 'hotel',
    travel_agent = 'travel-agent',
    TEMP_CACHED_ERROR_travel_agent = 'travel-agentl', // remove once HS cache has cleared out this url
    consumer = 'consumer'
}

export interface FormData {
    loginEmail?: string
    loginPassword?: string
    signupName?: string
    signupFirstName?: string
    signupLastName?: string
    signupEmail?: string
    signupPhoneNumber?: string
    signupPassword?: string
    signupTravelAgentType?: string
    signupCompanyName?: string
    signupConsortiumName?: string
    signupConsortiumMembershipNumber?: string
    signupBookingRevenue?: string
}

const userCredentials = 'user-credentials'

const formFields = {
    rememberMe: 'loginRememberMe',
    loginEmail: 'loginEmail',
    loginPassword: 'loginPassword',
    signupFirstname: 'signupFirstname',
    signupSurname: 'signupSurname',
    signupEmail: 'signupEmail',
    signupPassword: 'signupPassword',
    signupAutoLoginPassword: 'signupAutoLoginPassword',
    signupPhoneNumber: 'signupPhoneNumber',
    signupTravelAgentRole: 'signupTravelAgentRole',
    signupTravelAgentType: 'signupTravelAgentType',
    signupTermsConditions: 'termsAndConditions',
    signupCompanyWebsite: 'signupCompanyWebsite',
    signupCompanyName: 'signupCompanyName',
    signupAnnualBookingRevenue: 'signupAnnualBookingRevenue',
    signupPropertyDescription: 'signupPropertyDescription',
    signupConsortiumName: 'signupConsortiumName',
    signupConsortiumMembershipNumber: 'signupConsortiumMembershipNumber',
    passwordResetEmail: 'passwordResetEmail'
}

export const travelAgentType = {
    travelAgent: 'Travel Agent',
    tourOperator: 'Tour Operator',
    travelPlannerDesigner: 'Travel Planner / Designer',
    traveller: 'Traveller'
}

const hotelSignupThankYouPage = 'https://reservest.com/hotels/thankyou'
const guestSignupRedirect = 'https://reservest.com/guestenquiries'

@Component({
    selector: 'app-login',
    templateUrl: 'login.html'
})
export class LoginComponent extends UnsubscribeOnDestroy implements OnInit, AfterContentInit, OnChanges {

    @Input() role: Role 
    @Input() activeTab: number = 1
    @Input() label: string | undefined
    @Input() mobileDrawerOpen = false
    @Input() inviteCode: string | null
    @Input() formData: FormData | undefined
    @Input() onloadMessage: {
        message: string | null,
        type: 'error' | 'success' | null
    } | undefined
    @Input() existingUser: boolean
    @Input() selfSignup: SelfSignupRoles | undefined
    @Input() inviteAccepted: boolean = false
    @Input() isWhiteLabelSite = false

    resetPasswordForm: FormGroup
    loginForm: FormGroup
    signupForm: FormGroup
    signupInProgress: string | null
    signupSuccess = false
    signupError = false
    signupErrorMessage: string
    signupEmailValue: string
    signupPasswordValue: string
    loginInProgress: string | null
    loginError = false
    loginErrorMessage: string
    socialLoginError = false
    resetPasswordView = false
    resetPasswordInProgress = false
    resetPasswordSuccess = false
    resetPasswordSuccessMsg: string
    darkTheme: boolean
    mobileView: boolean
    loginCredentialsStoragekey = 'loginCredentials'
    tabButtons: tabButtonData[] = [
        { label: 'Login' },
        { label: 'Sign up' }
    ]
    LegalDocumentType = LegalDocumentType
    socialConnections = socialConnections
    SelfSignupRoles = SelfSignupRoles
    isSelfSingupForHotel: boolean
    isSelfSignupForTA: boolean
    isSelfSignupForConsumer: boolean
    Role = Role
    showLoginForExistingUser: boolean
    travelAgentType = travelAgentType
    isVerifiedTravelAgent = false // a opposed to traveller
    annualBookingRevenueOptions = [
        {
            value: AnnualBookingRevenue.LessThan_20000,
            label: '< 20 000 USD'
        },
        {
            value: AnnualBookingRevenue.Between_20000And_500000,
            label: '20 000 USD - 500 000 USD'
        },
        {
            value: AnnualBookingRevenue.GreaterThan_500000,
            label: '> 500 000 USD'
        }
    ]
    formContainer: Element

    @Output() mobileDrawerClosed = new EventEmitter<boolean>()
    @Output() tabSwitched = new EventEmitter<number>()

    constructor(
        private _amplitude: Amplitude,
        private _auth: Auth,
        private _breakpointObserver: BreakpointObserver,
        private _signupTravelAgent: SignupTravelAgentGQL,
        private _signupHotel: SignupHotelGQL,
    ) {
        super()

        this._breakpointObserver.observe([
            `(max-width: ${breakpoints.smMax})`
        ]).subscribe((result: BreakpointState) => {
            if (result.matches) {
                this.mobileView = true
                this.darkTheme = false
            } else {
                this.mobileView = false
                this.darkTheme = true
            }
        })
    }

    ngOnInit(): void {
        this.setSelfSignupType()
        this.showLoginForExistingUser = this.existingUser && !!this.role
        this.activeTab = this.activeTab || 1
        this.loginForm = new FormGroup({})
        this.signupForm = new FormGroup({})
        this.resetPasswordForm = new FormGroup({})
        this.onloadMessage = this.onloadMessage || { message: null, type: null }
        sessionStorage.removeItem(onboardingStorageKeys.inviteePhoneNumber)
        sessionStorage.removeItem(onboardingStorageKeys.signupTravelAgentRequest)
        this._auth.maintainSession(false)
        this.checkForStoredLoginCredentials()

        // invite
        if (this.role) {
            this.tabButtons = [
                { label: '', hidden: true },
                { label: 'Accept Invitation', disabled: true }
            ]
        }
        // Disable signup when not from invite link and no self_signup query param present
        else if (!this.selfSignup) {
            this.tabButtons[1].hidden = true
            this.tabButtons[0].disabled = true
        }
    }

    ngAfterContentInit(): void {
        if (this.showLoginForExistingUser) {
           this.handleDynamicFormControlsForExistingUser()
        }

        if (this.isSelfSignupForTA) {
            this.handleDynamicFormControlsForTravelAgentSignup()
        }

        // Cannot use ViewChild as form container is part of the tabs component
        this.formContainer = document.getElementsByClassName('tab-content')[0]
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.activeTab?.previousValue !== changes?.activeTab?.currentValue) {
            this.activeTab = changes.activeTab.currentValue
        }
    }

    private handleDynamicFormControlsForTravelAgentSignup() {
        this._subscriptions.push(
            this.signupForm.valueChanges.subscribe(formData => {
                this.isVerifiedTravelAgent = [
                    travelAgentType.travelAgent,travelAgentType.travelAgent,
                    travelAgentType.travelAgent,travelAgentType.tourOperator,
                    travelAgentType.travelAgent,travelAgentType.travelPlannerDesigner,
                ].includes(formData.signupTravelAgentRole)
            })
        )
    }

    private handleDynamicFormControlsForExistingUser() {
         // Dynamically add/remove form controls for switching password field type if login ot signup
         this._subscriptions.push(
            this.signupForm.valueChanges.subscribe(formData => {
        
                if (formData.signupEmail && formData.signupEmail !== this.formData?.signupEmail) {
                    const control = this.signupForm.get(formFields.signupAutoLoginPassword)

                    if (control) {
                        this.signupForm.removeControl(formFields.signupAutoLoginPassword)
                    }

                    this.showLoginForExistingUser = false
                    this.existingUser = false
                } 
                else {
                    const control = this.signupForm.get(formFields.signupPassword)

                    if (control) {
                        this.signupForm.removeControl(formFields.signupPassword)
                    }

                    this.showLoginForExistingUser = true
                    this.existingUser = true
                }
        }))
    }

    private checkForStoredLoginCredentials() {
        const storedLoginCredentials = sessionStorage.getItem(this.loginCredentialsStoragekey)

        if (storedLoginCredentials) {
            const loginCredentialsObj = JSON.parse(storedLoginCredentials)
            const email = loginCredentialsObj?.email

            if (email) {
                this.signupEmailValue = email
            }
        }
    }

    private rememberMe(email?: string) {
        if (this.loginForm?.get(formFields.rememberMe)?.value) {
            this._auth.maintainSession(true)
            if (email) {
                sessionStorage.setItem(this.loginCredentialsStoragekey, JSON.stringify({ email }))
            }
        } else {
            this._auth.maintainSession()
            sessionStorage.removeItem(this.loginCredentialsStoragekey)
        }
    }

    login() {
        if (!this.loginForm.valid) {
            return
        }

        const email = this.loginForm?.get(formFields.loginEmail)?.value
        const password = this.loginForm?.get(formFields.loginPassword)?.value

        if (email && password) {
            this.loginInProgress = userCredentials
            this.loginError = false

            this.rememberMe(email)

            this._auth.login(
                { email, password },
                this.onLoginError.bind(this)
            )
        }
    }

    socialLogin(socialConnection: string) {
        this.loginInProgress = socialConnection
        this.rememberMe()
        if (socialConnection) {
            this._auth.socialLogin(socialConnection)
        } else {
            this.loginInProgress = null
        }
    }

    socialSignup(socialConnection: string) {
        this.signupInProgress = socialConnection
        this.socialLoginError = false
        this.rememberMe()

        if (socialConnection && this.checkAndStorePhoneAndTerms()) {
            this._auth.socialLogin(socialConnection)
        } else {
            this.socialLoginError = true
            this.signupInProgress = null
        }
    }

    toggleResetPasswordView(value: boolean) {
        this.onloadMessage = undefined
        this.loginError = false
        this.resetPasswordView = value
    }

    resetPassword() {
        const email = this.resetPasswordForm?.get(formFields.passwordResetEmail)?.value
        if (email) {
            this.resetPasswordInProgress = true
            this._auth.resetPassword(email, this.onResetPasswordSuccess.bind(this))
        }
    }

    private autoLogin(email: string, password: string) {
        if (email && password) {
            this._auth.login(
                { email, password },
                this.onLoginError.bind(this)
            )
        }
    }

    private onLoginError(error?: Auth0Error) {
        this.loginInProgress = null
        this.signupInProgress = null
        this.onloadMessage = undefined
        this.loginError = true
        this.loginErrorMessage = error?.description || 'There was an error, please try again.'
        scrollToTop(this.formContainer)
    }

    signup() {
        if (!this.signupForm.valid) {
            return
        }

        this.signupError = false
        this.signupInProgress = userCredentials

        if (this.isSelfSignupForTA) {
            this.submitTravelAgentSignup()
        } else if (this.isSelfSingupForHotel) {
            this.submitHotelSignup()
        } else if (this.isSelfSignupForConsumer) {
            this.submitConsumerSignup()
        } else {
            this.auth0Signup()
        }
    }

    private getHubSpotContext(): TrackingContext {
        return {
            userId: getCookie('hubspotutk'),
            pageUri: window.location.href,
            pageName: window.location.href.split('/').slice(-1)[0]
        } as TrackingContext
    }

    private async submitConsumerSignup() {
        try {
            const phone = this.signupForm.get(formFields.signupPhoneNumber)?.value;
            const signupConsumerRequest = {
                userId: generateId('usr'),  // Add userId field
                firstName: this.signupForm.get(formFields.signupFirstname)?.value,
                lastName: this.signupForm.get(formFields.signupSurname)?.value,
                email: this.signupForm.get(formFields.signupEmail)?.value,
                phoneNumber: {
                    countryCode: phone.dialCode,
                    nationalNumber: phone.number
                }
            };

            // Store request in sessionStorage before Auth0 signup
            sessionStorage.setItem(onboardingStorageKeys.signupConsumerRequest, JSON.stringify(signupConsumerRequest));
            
            // Proceed with Auth0 signup
            this.auth0Signup();
            
        } catch (error) {
            this.signupError = true;
            this.signupErrorMessage = error?.description || 'There was an error creating your account, please try again.';
            this.signupInProgress = null;
        }
    }

    private submitHotelSignup() {
        let phone = this.signupForm.get(formFields.signupPhoneNumber)?.value
        const signupHotelRequest: SignupHotelRequest = {
            firstName: this.signupForm.get(formFields.signupFirstname)?.value,
            lastName: this.signupForm.get(formFields.signupSurname)?.value,
            email: this.signupForm.get(formFields.signupEmail)?.value,
            phoneNumber: {
                countryCode: phone.dialCode,
                nationalNumber: phone.number
            },
            website: this.signupForm.get(formFields.signupCompanyWebsite)?.value,
            propertyDescription: this.signupForm.get(formFields.signupPropertyDescription)?.value,
            context: this.getHubSpotContext(),
        }

        this._signupHotel
            .mutate({ input: signupHotelRequest })
            .subscribe(response => {
                if (response.errors && response.errors.length) {
                    console.log('Error submitting HubSpot form data', JSON.stringify(response.errors))
                    this.signupError = true
                    this.signupErrorMessage = 'There was an error, please try again.'
                    this.signupInProgress = null
                    this._amplitude.logEvent('User Signup Failed')
                    return
                }

                window.location.href = hotelSignupThankYouPage
            })
    }

    private submitTravelAgentSignup() {
        let phone = this.signupForm.get(formFields.signupPhoneNumber)?.value

        const signupTravelAgentRequest: SignupTravelAgentRequest = {
            firstName: this.signupForm.get(formFields.signupFirstname)?.value,
            lastName: this.signupForm.get(formFields.signupSurname)?.value,
            email: this.signupForm.get(formFields.signupEmail)?.value,
            phoneNumber: {
                countryCode: phone.dialCode,
                nationalNumber: phone.number
            },
            buyerType: this.signupForm.get(formFields.signupTravelAgentRole)?.value,
            companyName: this.signupForm.get(formFields.signupCompanyName)?.value,
            annualBookingRevenue: this.signupForm.get(formFields.signupAnnualBookingRevenue)?.value,
            consortiumName: this.signupForm.get(formFields.signupConsortiumName)?.value,
            consortiumMembershipNumber: this.signupForm.get(formFields.signupConsortiumMembershipNumber)?.value,
            context: this.getHubSpotContext(),
        }

        this.isVerifiedTravelAgent = [
            travelAgentType.travelAgent,
            travelAgentType.travelPlannerDesigner,
            travelAgentType.tourOperator,
        ].includes(signupTravelAgentRequest.buyerType)

        if (this.isVerifiedTravelAgent) {
            // auth0 signup-> login -> signup route to create account etc.
            sessionStorage.setItem(onboardingStorageKeys.signupTravelAgentRequest, JSON.stringify(signupTravelAgentRequest))
            this.auth0Signup()
        } 
        else {
            // Guest signup ie. Traveller
            this._signupTravelAgent
                .mutate({ input: signupTravelAgentRequest })
                .subscribe(response => {
                    if (response.errors && response.errors.length) {
                        console.log('Error submitting HubSpot form data', JSON.stringify(response.errors))
                        this.signupError = true
                        this.signupErrorMessage = 'There was an error, please try again.'
                        this.signupInProgress = null
                        this._amplitude.logEvent('User Signup Failed')
                        scrollToTop(this.formContainer)
                        return
                    }

                    window.location.href = guestSignupRedirect
                })
        }
    }

    private auth0Signup() {
        const email = this.signupForm.get(formFields.signupEmail)?.value
        const password = this.signupForm.get(formFields.signupPassword)?.value || this.signupForm.get(formFields.signupAutoLoginPassword)?.value
        console.log('ENV: ', environment, email, password)

        if (email && password) {
            // If user already has auth0 account log them in and proceed to inviation process
            if (this.existingUser && this.inviteCode) {
                if (this.checkAndStorePhoneAndTerms()) {

                    if (this.inviteAccepted) {
                        // Remove these so that on authenticate route it does not try to sign user up but logs them in
                        sessionStorage.removeItem(onboardingStorageKeys.inviteCode)
                        sessionStorage.removeItem(onboardingStorageKeys.inviteRole)
                        sessionStorage.removeItem(onboardingStorageKeys.inviteAsset)
                    }
                    // Login and then proceed to process invitation
                    this.autoLogin(email, password)
                }
                return
            }

            this._auth.signup(
                { email, password },
                this.onSignupSuccess.bind(this, email, password),
                this.onSignupError.bind(this)
            )
        } else {
            this.onLoginError()
        }
    }

    private checkAndStorePhoneAndTerms(): boolean {
        const phoneControl = this.signupForm.get(formFields.signupPhoneNumber)

        if (!phoneControl?.errors) {
            const phoneNumberObj = phoneControl?.value
            const termsAndConditionsAccepted = this.signupForm.get(formFields.signupTermsConditions)?.value

            if (termsAndConditionsAccepted && phoneNumberObj) {
                sessionStorage.setItem(onboardingStorageKeys.inviteePhoneNumber, JSON.stringify({
                    dialCode: phoneNumberObj.dialCode,
                    nationalNumber: phoneNumberObj.nationalNumber
                }))

                return true
            }
        }
        return false
    }

    private onSignupSuccess(email: string, password: string) {
        setTimeout(() => {
            // Invite link
            if (this.inviteCode || this.isSelfSignupForTA || this.isSelfSignupForConsumer) {
                if (this.checkAndStorePhoneAndTerms()) {
                    this.autoLogin(email, password)
                }
            }
            // self signup
            else {
                this.loginForm.controls[formFields.loginEmail].setValue(email)
                this.loginForm.controls[formFields.loginPassword].setValue(password)
                this.activeTab = 1
                this.signupForm.reset()
                this.signupInProgress = null
                this.signupSuccess = true
            }
        }, 1000)
    }

    private onSignupError(error: Auth0Error) {
        setTimeout(() => {
            this.signupError = true
            this.signupInProgress = null

            if (error?.description && error?.code === 'invalid_signup') {
                this.signupErrorMessage = error?.description + '. This email address may already be in use.'
                this._amplitude.logEvent('User Email Already In Use')
            } else {
                this.signupErrorMessage = error?.description || 'There was an error, please try again.'
                this._amplitude.logEvent('User Signup Error Occurred')
            }
            scrollToTop(this.formContainer)

        }, 1000)
    }

    private onResetPasswordSuccess() {
        setTimeout(() => {
            this.resetPasswordInProgress = false
            this.resetPasswordSuccess = true
        }, 1000)
    }

    trackCurrentTab(tabIndex: number) {
        this.resetPasswordView = false
        this.tabSwitched.emit(tabIndex)
    }

    closeMobileDrawer() {
        this.mobileDrawerClosed.emit(true)
    }

    setSelfSignupType() {
        this.isSelfSignupForTA = this.selfSignup === SelfSignupRoles.travel_agent || this.selfSignup === SelfSignupRoles.TEMP_CACHED_ERROR_travel_agent
        this.isSelfSingupForHotel = this.selfSignup === SelfSignupRoles.hotel
        this.isSelfSignupForConsumer = this.selfSignup === SelfSignupRoles.consumer
    }
}
